// ─── CyberHealth Scanner — API Client ─────────────────────────────────────────
import axios, { AxiosError } from 'axios';
import type { ScanResult } from '../types/scanner';
import { getCurrentLang } from '../i18n/LanguageContext';

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8000';

// ── Type : quota hebdomadaire ─────────────────────────────────────────────────
export interface RateLimitInfo {
  type:      'anonymous' | 'free' | 'unlimited';
  limit:     number | null;
  used:      number;
  remaining: number | null;
  day_key:   string;
}

export const apiClient = axios.create({
  baseURL:         BASE_URL,
  timeout:         60_000,  // 60s — les scans de ports peuvent être longs
  headers:         { 'Content-Type': 'application/json' },
  withCredentials: true,    // envoie le cookie wezea_cid (HttpOnly) cross-origin
});

// Injecter le token JWT automatiquement
apiClient.interceptors.request.use(config => {
  const token = localStorage.getItem('wezea_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * À appeler une fois au démarrage de l'app.
 * Déclenche GET /client-id → le backend pose un cookie HttpOnly `wezea_cid`
 * si ce n'est pas déjà fait. Ce cookie sert à identifier les visiteurs anonymes
 * de façon sécurisée (non spoofable depuis JS).
 */
export async function initClientId(): Promise<void> {
  try {
    await apiClient.get('/client-id');
  } catch {
    // Silencieux — le fallback IP prend le relais si le cookie échoue
  }
}

// ── Endpoints ─────────────────────────────────────────────────────────────────

export async function scanDomain(domain: string, lang: string = 'fr'): Promise<ScanResult> {
  const { data } = await apiClient.post<ScanResult>('/scan', { domain, lang });
  return data;
}

export async function requestFullReport(payload: {
  domain:      string;
  email:       string;
  first_name?: string;
  last_name?:  string;
  company?:    string;
  lang?:       string;
  scan_data?:  Record<string, unknown> | null;
}): Promise<{ lead_id: string; scan_id?: string; message: string }> {
  // Langue du rapport PDF + email livré au lead = langue de l'UI (sauf override).
  const { data } = await apiClient.post('/report/request', { lang: getCurrentLang(), ...payload });
  return data;
}

/**
 * Génère le rapport PDF et retourne un Blob téléchargeable.
 * Envoie le JSON complet du scan à POST /generate-pdf.
 */
export async function generatePDF(scanResult: ScanResult, lang: string = 'fr'): Promise<Blob> {
  const response = await apiClient.post('/generate-pdf', { ...scanResult, lang }, {
    responseType: 'blob',
    timeout:      90_000,   // WeasyPrint peut nécessiter plus de temps
  });
  return new Blob([response.data], { type: 'application/pdf' });
}

/**
 * Déclenche le téléchargement navigateur d'un Blob PDF.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url  = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href     = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// ── Récupérer le quota hebdomadaire ───────────────────────────────────────────

export async function getScanLimits(): Promise<RateLimitInfo> {
  const { data } = await apiClient.get<RateLimitInfo>('/scan/limits');
  return data;
}

// ── Error helpers ─────────────────────────────────────────────────────────────

export function extractApiError(err: unknown): string {
  if (err instanceof AxiosError) {
    const detail = err.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (detail?.message) return detail.message;
    if (err.response?.status === 422) return 'Domaine invalide. Vérifiez le format (ex: exemple.fr).';
    if (err.response?.status === 429) return detail?.message ?? "Limite de scans atteinte pour aujourd'hui.";
    if (err.code === 'ECONNABORTED') return 'Le scan a pris trop de temps. Réessayez.';
  }
  return 'Erreur inattendue. Veuillez réessayer.';
}

/** Extrait les infos de rate limit d'une erreur 429. */
export function extractRateLimitDetail(err: unknown): (RateLimitInfo & { message?: string }) | null {
  if (err instanceof AxiosError && err.response?.status === 429) {
    const detail = err.response?.data?.detail;
    if (detail?.limit !== undefined) {
      return detail as RateLimitInfo & { message?: string };
    }
  }
  return null;
}

// ── White-label API ───────────────────────────────────────────────────────────

export interface WhiteLabelSettings {
  enabled:       boolean;
  company_name:  string | null;
  primary_color: string | null;
  has_logo:      boolean;
  logo_b64:      string | null;
}

export async function getWhiteLabel(): Promise<WhiteLabelSettings> {
  const { data } = await apiClient.get<WhiteLabelSettings>('/auth/white-label');
  return data;
}

export async function updateWhiteLabel(payload: {
  enabled?:       boolean;
  company_name?:  string;
  primary_color?: string;
}): Promise<WhiteLabelSettings> {
  const { data } = await apiClient.patch<WhiteLabelSettings>('/auth/white-label', payload);
  return data;
}

export async function uploadWhiteLabelLogo(file: File): Promise<{ has_logo: boolean; size_kb: number }> {
  const form = new FormData();
  form.append('file', file);
  // Utiliser fetch natif : apiClient a un default 'Content-Type: application/json'
  // qui écrase le multipart/form-data nécessaire pour l'upload.
  // fetch() laisse le browser gérer automatiquement la boundary multipart.
  const token = localStorage.getItem('wezea_token');
  const res = await fetch(`${BASE_URL}/auth/white-label/logo`, {
    method:  'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body:    form,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    throw { response: { data: err } };
  }
  return res.json();
}

export async function deleteWhiteLabelLogo(): Promise<void> {
  await apiClient.delete('/auth/white-label/logo');
}

// ── Accepted Risks API ─────────────────────────────────────────────────────────

export interface AcceptedRiskItem {
  id:                number;
  domain:            string;
  finding_key:       string;
  category:          string;
  title:             string;
  severity:          string;
  justification:     string;
  accepted_by_email: string;
  accepted_at:       string;
}

export async function getAcceptedRisks(domain?: string): Promise<AcceptedRiskItem[]> {
  const params = domain ? { domain: domain.toLowerCase() } : {};
  const { data } = await apiClient.get<AcceptedRiskItem[]>('/accepted-risks', { params });
  return data;
}

export async function acceptRisk(payload: {
  domain:        string;
  category:      string;
  title:         string;
  severity:      string;
  justification: string;
}): Promise<AcceptedRiskItem> {
  const { data } = await apiClient.post<AcceptedRiskItem>('/accepted-risks', payload);
  return data;
}

export async function revokeAcceptedRisk(id: number): Promise<void> {
  await apiClient.delete(`/accepted-risks/${id}`);
}

// ── EASM — Surface d'attaque (Jalon 1 : découverte passive) ─────────────────────

export type AssetType   = 'subdomain' | 'ip' | 'service' | 'cert';
export type AssetStatus = 'active' | 'dangling' | 'third_party' | 'removed';

export interface SurfaceAsset {
  id:          number;
  seed_domain: string;
  asset_type:  AssetType;
  value:       string;
  status:      AssetStatus;
  is_owned:    boolean;
  source:      string;
  metadata:    Record<string, unknown>;
  score:       number | null;
  first_seen:  string | null;
  last_seen:   string | null;
}

export interface SurfaceCounts {
  total:     number;
  exposed:   number;
  by_type:   Record<string, number>;
  by_status: Record<string, number>;
  wildcard:  boolean;
}

export interface SurfaceDiscoverResponse {
  seed_domain:       string;
  run_id:            number;
  counts:            SurfaceCounts;
  assets_count:      number;
  exposed_count:     number;
  wildcard_detected: boolean;
  assets:            SurfaceAsset[];
  errors:            Record<string, string>;
}

export type SurfaceFrequency = 'weekly' | 'biweekly' | 'monthly';

export interface SurfaceMonitor {
  seed_domain:    string;
  is_active:      boolean;
  frequency:      SurfaceFrequency;
  email_alerts:   boolean;
  last_run_at:    string | null;
  assets_count:   number | null;
  exposed_count:  number | null;
  dangling_count: number | null;
}

export interface SurfaceInventoryResponse {
  seed_domain: string;
  total:       number;
  count:       number;
  offset:      number;
  limit:       number;
  assets:      SurfaceAsset[];
  last_run:    {
    id:          number;
    started_at:  string | null;
    finished_at: string | null;
    counts:      SurfaceCounts;
    trigger:     string;
  } | null;
  monitor:     SurfaceMonitor | null;
}

export type SurfaceChangeType = 'new' | 'removed' | 'status_changed';

export interface SurfaceChange {
  id:          number;
  change_type: SurfaceChangeType;
  asset_type:  AssetType;
  value:       string;
  detail:      Record<string, unknown>;
  created_at:  string | null;
}

export interface SurfaceChangesResponse {
  seed_domain: string;
  total:       number;
  count:       number;
  offset:      number;
  limit:       number;
  changes:     SurfaceChange[];
}

/** Lance une découverte passive et persiste l'inventaire (authentifié). */
export async function discoverSurface(seedDomain: string): Promise<SurfaceDiscoverResponse> {
  const { data } = await apiClient.post<SurfaceDiscoverResponse>('/surface/discover', { seed_domain: seedDomain });
  return data;
}

/** Récupère l'inventaire courant d'un domaine (filtrable / paginé). */
export async function getSurfaceInventory(
  seedDomain: string,
  opts: { asset_type?: AssetType; status?: AssetStatus; limit?: number; offset?: number } = {},
): Promise<SurfaceInventoryResponse> {
  const { data } = await apiClient.get<SurfaceInventoryResponse>(
    `/surface/${encodeURIComponent(seedDomain)}`,
    { params: opts },
  );
  return data;
}

/** Journal des changements d'inventaire (surveillance). */
export async function getSurfaceChanges(
  seedDomain: string,
  opts: { limit?: number; offset?: number } = {},
): Promise<SurfaceChangesResponse> {
  const { data } = await apiClient.get<SurfaceChangesResponse>(
    `/surface/${encodeURIComponent(seedDomain)}/changes`,
    { params: opts },
  );
  return data;
}

/** Active la surveillance continue d'un domaine (Starter+). */
export async function enableSurfaceMonitor(
  seedDomain: string,
  body: { frequency?: SurfaceFrequency; email_alerts?: boolean } = {},
): Promise<SurfaceMonitor> {
  const { data } = await apiClient.post<SurfaceMonitor>(
    `/surface/${encodeURIComponent(seedDomain)}/monitor`, body,
  );
  return data;
}

/** Désactive la surveillance continue d'un domaine. */
export async function disableSurfaceMonitor(seedDomain: string): Promise<void> {
  await apiClient.delete(`/surface/${encodeURIComponent(seedDomain)}/monitor`);
}

// ── Scan actif (Jalon 3 : preuve de propriété + scan léger) ──────────────────

export type OwnershipMethod = 'dns' | 'file';

export interface SurfaceOwnership {
  seed_domain:  string;
  is_verified:  boolean;
  method:       OwnershipMethod;
  verified_at:  string | null;
  instructions: Record<string, string>;
}

export interface ActiveAssetResult {
  host:           string;
  score:          number;
  risk_level:     string;
  findings_count: number;
  critical:       number;
  high:           number;
  open_ports:     number[];
}

export interface ActiveScanResponse {
  seed_domain:   string;
  scanned_hosts: number;
  skipped:       number;
  results:       ActiveAssetResult[];
}

/** Statut + instructions de preuve de propriété (pré-requis au scan actif). */
export async function getSurfaceOwnership(
  seedDomain: string, method: OwnershipMethod = 'dns',
): Promise<SurfaceOwnership> {
  const { data } = await apiClient.get<SurfaceOwnership>(
    `/surface/${encodeURIComponent(seedDomain)}/ownership`, { params: { method } },
  );
  return data;
}

/** Déclenche la vérification de propriété (DNS TXT ou fichier). */
export async function verifySurfaceOwnership(
  seedDomain: string, method: OwnershipMethod = 'dns',
): Promise<SurfaceOwnership> {
  const { data } = await apiClient.post<SurfaceOwnership>(
    `/surface/${encodeURIComponent(seedDomain)}/ownership/verify`, { method },
  );
  return data;
}

/** Lance le scan actif léger des actifs possédés + vérifiés (Starter+). */
export async function scanSurfaceActive(seedDomain: string): Promise<ActiveScanResponse> {
  const { data } = await apiClient.post<ActiveScanResponse>(
    `/surface/${encodeURIComponent(seedDomain)}/scan-active`,
  );
  return data;
}

export interface SurfacePreview {
  seed_domain:   string;
  assets_count:  number;
  exposed_count: number;
  share_token:   string;
}

/** Hook PLG : preview anonyme — compteurs agrégés uniquement (aucun détail). */
export async function surfacePreview(domain: string): Promise<SurfacePreview> {
  const { data } = await apiClient.post<SurfacePreview>('/public/surface/preview', { domain });
  return data;
}
